import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getOverview, getFilterOptions, getApplications } from "@/lib/api";
import { useAppStore } from "@/store/appStore";
import { STREAM_COLORS } from "@/lib/format";
import { RingGauge } from "@/components/tmo/GaugeRadial";
import { SectionCard, StatusChip, DriverBar, SampleDataTag } from "@/components/tmo/primitives";
import { PieChart, Pie, Cell } from "recharts";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Users, HardHat, Briefcase, Share2, Cloud, Gauge as GaugeIcon, ClipboardCheck, Database,
  TriangleAlert, Target, Headset, Settings, ChevronUp, ChevronDown, HelpCircle,
  Clock, CheckCircle2, ShieldCheck, BarChart3, Info, OctagonAlert, ListChecks, Activity,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const STREAM_ICON = { SUBSCRIBER: Users, TECHNICIAN: HardHat, EXECUTIVE: Briefcase, SHARED: Share2 };
const CAP_ICON = { availability: Cloud, performance: GaugeIcon, test_coverage: ClipboardCheck,
  dependencies: Share2, data_freshness: Database, issues: TriangleAlert };
const DRIVER_ICON = { recent_testing: Clock, test_success: CheckCircle2, performance: GaugeIcon,
  dependencies: Share2, data_freshness: Database, open_issues: TriangleAlert, availability: ShieldCheck };
const PRIORITY_ICON = { target: Target, alert: TriangleAlert, coverage: ClipboardCheck, evidence: Database, headset: Headset, settings: Settings };
const ISSUE_ICON = { critical: OctagonAlert, risk: TriangleAlert, open: ListChecks, atrisk: Activity };
const MAP_META = {
  SUBSCRIBER: { label: "Subscriber / Customer", icon: Users, desc: "Customer-facing channels: web, mobile, IVR, notifications." },
  TECHNICIAN: { label: "Technician / Field", icon: HardHat, desc: "Field crew tools: dispatch, OMS/ADMS, damage assessment, gas ops." },
  EXECUTIVE: { label: "Executive / Leadership", icon: Briefcase, desc: "Decision support: analytics, dashboards, reporting pipelines." },
  SHARED: { label: "Shared / Infrastructure", icon: Share2, desc: "Platform & infrastructure serving every stream (network, EAI, identity)." },
};

function StreamCard({ card, onClick }) {
  const Icon = STREAM_ICON[card.stream];
  const color = STREAM_COLORS[card.stream];
  return (
    <div data-testid={`stream-card-${card.stream}`} onClick={onClick}
      className="bg-cnp-card rounded-lg border border-cnp-border shadow-sm p-5 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col items-center text-center">
      <span className="text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color }}>{card.label}</span>
      <div className="flex items-center gap-3">
        <div className="h-14 w-14 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: color }}>
          <Icon size={26} className="text-white" />
        </div>
        <div className="text-left">
          <UITooltip>
            <TooltipTrigger asChild>
              <div className="text-[40px] leading-none font-bold tnum text-cnp-navy cursor-help">{card.score}%</div>
            </TooltipTrigger>
            <TooltipContent className="w-56">
              <div className="text-[11px] font-semibold mb-1">Why {card.score}% — Testing Readiness Index</div>
              {card.drivers && Object.entries(card.drivers).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[11px]"><span className="text-cnp-textMuted">{k}</span><span className="tnum font-semibold">{v}%</span></div>
              ))}
              <div className="text-[10px] text-cnp-textMuted mt-1">Criticality-weighted mean across {card.total} apps · T1 40% · T4 30% · T2 15% · T3 15%</div>
            </TooltipContent>
          </UITooltip>
          <div className="text-[10px] uppercase tracking-wide text-cnp-textMuted">Confidence</div>
        </div>
      </div>
      <div className="mt-3"><StatusChip status={card.status} size="lg" /></div>
      <div className="w-full border-t border-cnp-border mt-4 pt-3">
        <div className="text-xl font-bold tnum text-cnp-navy">{card.ready} of {card.total}</div>
        <div className="text-[11px] text-cnp-textMuted">Applications Ready</div>
      </div>
    </div>
  );
}

export default function Overview() {
  const scenario = useAppStore((s) => s.scenario);
  const openExplain = useAppStore((s) => s.openExplain);
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["overview", scenario], queryFn: () => getOverview(scenario) });
  const [director, setDirector] = useState("All");
  const { data: filterOpts } = useQuery({ queryKey: ["filter-options"], queryFn: getFilterOptions });
  const { data: dirApps } = useQuery({
    queryKey: ["glance-apps", scenario, director],
    enabled: director !== "All",
    queryFn: () => getApplications({ scenario, director, page_size: 500 }),
  });

  if (isLoading || !data) {
    return <div className="space-y-4"><Skeleton className="h-64 rounded-lg" /><div className="grid grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-lg" />)}</div></div>;
  }

  const o = data.overall;
  const dist = data.distribution;
  const drivers = data.drivers.filter((d) => d.id !== "availability");

  // Applications-at-a-glance distribution, optionally filtered by Director
  let glance = { total: dist.total, ready: dist.ready, at_risk: dist.at_risk, not_ready: dist.not_ready, na: dist.na };
  if (director !== "All" && dirApps?.rows) {
    const rows = dirApps.rows;
    glance = {
      total: rows.length,
      ready: rows.filter((r) => r.status === "READY").length,
      at_risk: rows.filter((r) => r.status === "AT_RISK").length,
      not_ready: rows.filter((r) => r.status === "NOT_READY").length,
      na: rows.filter((r) => r.status === "NA").length,
    };
  }
  const donut = [
    { name: "Ready (Pass)", value: glance.ready, color: "#00A651" },
    { name: "At Risk (Caution)", value: glance.at_risk, color: "#F5A623" },
    { name: "Not Ready (Fail)", value: glance.not_ready, color: "#D0021B" },
  ];
  if (glance.na) donut.push({ name: "N/A", value: glance.na, color: "#9BA3AF" });

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-5" data-testid="overview-page">
      {/* 1 — Overall score + drivers */}
      <div className="bg-cnp-card rounded-lg border border-cnp-border shadow-sm p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-1.5 mb-4">
            <h3 className="text-[13px] uppercase tracking-wider font-semibold text-cnp-blue">Overall Readiness Score</h3>
            <button onClick={() => openExplain({ type: "overview", title: "Overall Readiness" })} className="text-cnp-blue"><HelpCircle size={14} /></button>
            <SampleDataTag className="ml-auto" />
          </div>
          <div className="flex items-center gap-6 justify-center lg:justify-start">
            <RingGauge value={o.score} status={o.status} size={190} stroke={18} />
            <div>
              <StatusChip status={o.status} size="lg" />
              <div className="text-3xl font-bold tnum text-cnp-navy mt-3">{dist.ready} of {dist.scored}</div>
              <div className="text-sm text-cnp-textMuted">Applications Ready</div>
            </div>
          </div>
        </div>
        <div className="lg:border-l lg:border-cnp-border lg:pl-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-[13px] uppercase tracking-wider font-semibold text-cnp-blue">Applications At A Glance</h3>
            <div className="flex items-center gap-2">
              <SampleDataTag />
              <select
              data-testid="glance-director-filter"
              value={director}
              onChange={(e) => setDirector(e.target.value)}
              className="text-xs rounded-md border border-cnp-border bg-white px-2 py-1 text-cnp-text focus:outline-none focus:ring-2 focus:ring-cnp-blue/40 max-w-[200px]"
            >
              <option value="All">All Directors</option>
              {(filterOpts?.director || []).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-center shrink-0">
              <div className="text-[44px] leading-none font-bold tnum text-cnp-navy">{glance.total}</div>
              <div className="text-[11px] text-cnp-textMuted mt-1">{director === "All" ? "Total Applications" : "Applications"}</div>
            </div>
            <div className="relative shrink-0">
              {glance.total === 0 ? (
                <div className="h-[130px] w-[130px] flex items-center justify-center text-xs text-cnp-textMuted">No apps</div>
              ) : (
                <PieChart width={130} height={130}>
                  <Pie data={donut} dataKey="value" innerRadius={42} outerRadius={62} paddingAngle={2} stroke="none">
                    {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              )}
            </div>
            <div className="flex-1 space-y-2">
              {donut.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />{d.name}</span>
                  <span className="tnum font-semibold text-cnp-navy">{d.value} <span className="text-cnp-textMuted font-normal">({glance.total ? Math.round((d.value / glance.total) * 100) : 0}%)</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 2 — Readiness by stream */}
      <div>
        <h3 className="text-[13px] uppercase tracking-wider font-semibold text-cnp-blue">Readiness By Stream</h3>
        <p className="text-xs text-cnp-textMuted mb-2">Our four streams power overall readiness</p>
        <div className="relative h-4">
          <div className="absolute left-[12%] right-[12%] top-2 border-t-2 border-cnp-border" />
          <ChevronUp size={16} className="absolute left-1/2 -translate-x-1/2 -top-1 text-cnp-border" strokeWidth={3} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {data.stream_cards.map((c) => <StreamCard key={c.stream} card={c} onClick={() => navigate(c.stream === "SHARED" ? "/shared" : `/${c.stream.toLowerCase()}`)} />)}
        </div>
      </div>

      {/* 3 — capability health */}
      <SectionCard title="Key Capability Health">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-1 divide-x divide-cnp-border">
          {data.capabilities.map((c) => {
            const Icon = CAP_ICON[c.id] || Cloud; const healthy = c.value >= 70;
            return (
              <UITooltip key={c.id}>
                <TooltipTrigger asChild>
                  <div className="flex flex-col items-center text-center px-1.5 cursor-help">
                    <Icon size={18} className="text-cnp-textMuted mb-1.5" />
                    <span className="text-[9px] uppercase tracking-wide font-semibold text-cnp-text leading-tight h-6">{c.label}</span>
                    <div className="text-[26px] leading-none font-bold tnum text-cnp-navy mt-1">{c.value}%</div>
                    <div className="text-[11px] font-semibold mt-1" style={{ color: healthy ? "#00A651" : "#F5A623" }}>{healthy ? "Healthy" : "Degraded"}</div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="w-52">
                  <div className="text-[11px] font-semibold mb-1">Why {c.value}%</div>
                  {c.metrics.map((m, i) => (
                    <div key={i} className="flex justify-between text-[11px]"><span className="text-cnp-textMuted">{m.label}</span><span className="tnum font-semibold">{m.value}</span></div>
                  ))}
                </TooltipContent>
              </UITooltip>
            );
          })}
        </div>
      </SectionCard>

      {/* 5 — what's driving + issues & risks summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="What's Driving Readiness">
          <div className="space-y-2.5">
            {data.driving.map((d, i) => {
              const Icon = d.direction === "negative" ? ChevronDown : ChevronUp;
              const col = d.direction === "positive" ? "#00A651" : d.direction === "caution" ? "#F5A623" : "#D0021B";
              return <div key={i} className="flex items-center gap-2 text-sm"><Icon size={16} style={{ color: col }} strokeWidth={3} /><span className="text-cnp-text">{d.text}</span></div>;
            })}
          </div>
        </SectionCard>

        <SectionCard title="Testing Readiness Summary" className="lg:col-span-2">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {data.issues_summary.map((it) => {
              const Icon = ISSUE_ICON[it.icon] || TriangleAlert;
              return (
                <div key={it.label} onClick={() => navigate("/issues")} className="rounded-lg border border-cnp-border p-3 flex flex-col items-center text-center cursor-pointer hover:shadow-md transition-all">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center mb-1.5" style={{ backgroundColor: `${it.color}1A` }}><Icon size={19} style={{ color: it.color }} /></div>
                  <div className="text-[28px] leading-none font-bold tnum text-cnp-navy">{it.count}</div>
                  <div className="text-[11px] font-semibold text-cnp-text mt-1">{it.label}</div>
                  <div className="text-[10px] text-cnp-textMuted leading-tight mt-0.5">{it.sub}</div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* 6 — key priorities */}
      <SectionCard title="Key Priorities">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.priorities.map((p, i) => {
            const Icon = PRIORITY_ICON[p.icon] || Target;
            const col = p.icon === "alert" ? "#F5A623" : p.icon === "target" ? "#00A651" : p.icon === "headset" ? "#7B4FA8" : "#0075C9";
            return (
              <div key={i} onClick={() => navigate("/applications")} className="rounded-lg border border-cnp-border p-3 cursor-pointer hover:shadow-md transition-all">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${col}1A` }}><Icon size={20} style={{ color: col }} /></div>
                  {p.count != null && <span className="text-[32px] leading-none font-bold tnum text-cnp-navy">{p.count}</span>}
                </div>
                <div className="text-xs font-medium text-cnp-text leading-tight">{p.label}</div>
                <div className="text-[11px] text-cnp-textMuted mt-1">{p.action}</div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* info note */}
      <div className="bg-cnp-blue/5 border border-cnp-blue/20 rounded-lg px-4 py-3 flex items-center gap-3">
        <Info size={18} className="text-cnp-blue shrink-0" />
        <p className="text-sm text-cnp-text">Readiness confidence reflects the combined health of people, processes, applications, and infrastructure required to deliver reliable service.</p>
      </div>
    </div>
    </TooltipProvider>
  );
}
